import { _, hooks, mui, React, ts, useSelector } from '_core';

import * as prefColumns from 'views/preferences/columns-config';
import Tabular from 'views/report/tables/tabular';

import { Params, SHEETS } from './types';
import { getColumns } from '../../common/utils/chart-utils';

type ChartProps = {
  id: string;
  params: Params;
  setParams: (_v: Params) => void;
  portfolios: string;
  alphaExposures: string;
  fullScreen: boolean;
  queryParams: { initial?: string };
  context?: ts.types.backtest.BacktestAnaylsisContext | ts.types.basket.BasketAnalysisContext;
  goFullScreen: (_v: string) => void;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
};

const Chart = ({
  id,
  params,
  setParams,
  context,
  portfolios,
  alphaExposures,
  fullScreen,
  queryParams,
  goFullScreen,
  loadWidgetData,
}: ChartProps) => {
  const resources = useSelector((state) => state.resources);

  const yearlySummarySheetKey = ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.BACKTEST_TABULAR_YEARLY_SUMMARY;
  const monthlySummarySheetKey = ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.BACKTEST_TABULAR_MONTHLY_SUMMARY;
  const detailsSheetKey = ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.BACKTEST_TABULAR_DETAILS;
  const alphaDetailsSheetKey = ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.BACKTEST_TABULAR_ALPHA_DETAILS;

  const yearlySummarySheetName = ts.enums.REPORT_ENUMS.TABLE_SHEET_PREF_NAME_ENUM.BACKTEST_TABULAR_YEARLY_SUMMARY;
  const monthlySummarySheetName = ts.enums.REPORT_ENUMS.TABLE_SHEET_PREF_NAME_ENUM.BACKTEST_TABULAR_MONTHLY_SUMMARY;
  const detailsSheetName = ts.enums.REPORT_ENUMS.TABLE_SHEET_PREF_NAME_ENUM.BACKTEST_TABULAR_DETAILS;
  const alphaDetailsSheetName = ts.enums.REPORT_ENUMS.TABLE_SHEET_PREF_NAME_ENUM.BACKTEST_TABULAR_ALPHA_DETAILS;

  const isBasket =
    params.isBasket ||
    // using context?.basket_id for backward compatibility
    (context as ts.types.basket.BasketAnalysisContext)?.basket_id;

  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);

  const [tableParams, setTableParams] = React.useState<ts.types.components.dataGrid.TableParamsSheet>(
    params.table_params
  );

  hooks.useEffectWithoutFirst(() => {
    setParams({
      ...params,
      table_params: { ...tableParams },
    });
  }, [tableParams]);

  const container = React.createRef();

  const loadAlphaExposuresData = async () => {
    const response = await loadWidgetData({
      data: {
        file: alphaExposures,
      },
    });
    if (response) {
      return response.data;
    }

    return null;
  };

  const loadPortfoliosData = async () => {
    const response = await loadWidgetData({
      data: {
        file: portfolios,
      },
    });
    if (response) {
      const localData = response.data;
      return {
        [yearlySummarySheetKey]: localData.filter((el) => el.tab == 'yearly_summary'),
        [monthlySummarySheetKey]: localData.filter((el) => el.tab == monthlySummarySheetKey),
        [detailsSheetKey]: localData.filter((el) => el.tab == detailsSheetKey),
      };
    }
  };

  const loadData = async () => {
    try {
      const portfoliosDataResp = await loadPortfoliosData();
      const alphaExposuresData = await loadAlphaExposuresData();
      if (portfoliosDataResp && alphaExposuresData) {
        setData({ ...portfoliosDataResp, [alphaDetailsSheetKey]: alphaExposuresData });
      }
    } catch (err) {
      setError(JSON.stringify(err));
    }
  };

  React.useEffect(() => {
    if (portfolios && alphaExposures) loadData();
    return () => {
      setData(null);
    };
  }, [portfolios, alphaExposures]);

  const columnValidation = (columns: ts.types.components.dataGrid.ColumnsData) => {
    let localColumns = [...columns];

    if (_.isEmpty(context.definition?.benchmark))
      localColumns = localColumns.filter((c) => !c.key.includes('benchmark_correlation'));
    if (!(context as ts.types.backtest.BacktestAnaylsisContext)?.definition.signal_id)
      localColumns = localColumns.filter((c) => !c.key.includes('alpha_correlation'));
    if (context.definition?.optimizer_config?.objective_function?.mean_variance_type != 'default')
      localColumns = localColumns.filter((c) => !c.key.includes('cp_correlation'));

    return localColumns;
  };

  const columnRename = (columns: ts.types.components.dataGrid.ColumnsData) => {
    if (!isBasket) return columns;
    return columns.map((c) => {
      c.name = (c.name as string).replace('Benchmark', 'Unoptimized Basket');
      return c;
    });
  };

  const columns = isBasket ? prefColumns.basketTabularOutputColumns : prefColumns.backtestTabularOutputColumns;

  const getColumnsObj = React.useMemo(() => {
    const obj: Record<string, ts.types.components.dataGrid.GenerateColumns> = {
      [yearlySummarySheetKey]: ((_k, preferences) =>
        getColumns(
          columns[yearlySummarySheetKey].map((el) => el.key),
          preferences?.[yearlySummarySheetKey],
          columns[yearlySummarySheetKey],
          null,
          null,
          [],
          columnValidation,
          columnRename
        )) as ts.types.components.dataGrid.GenerateColumns,
      [monthlySummarySheetKey]: ((_k, preferences) =>
        getColumns(
          columns[monthlySummarySheetKey].map((el) => el.key),
          preferences?.[monthlySummarySheetKey],
          columns[monthlySummarySheetKey],
          null,
          null,
          [],
          columnValidation,
          columnRename
        )) as ts.types.components.dataGrid.GenerateColumns,
      [detailsSheetKey]: ((_k, preferences) =>
        getColumns(
          columns[detailsSheetKey].map((el) => el.key),
          preferences?.[detailsSheetKey],
          columns[detailsSheetKey],
          null,
          null,
          [],
          columnValidation,
          columnRename
        )) as ts.types.components.dataGrid.GenerateColumns,
    };

    if (!isBasket) {
      obj[alphaDetailsSheetKey] = ((colKeys, preferences) =>
        getColumns(
          colKeys,
          preferences?.[alphaDetailsSheetKey],
          columns[alphaDetailsSheetKey],
          null,
          resources
        )) as ts.types.components.dataGrid.GenerateColumns;
    }

    return obj;
  }, [resources, columns, isBasket]);

  return (
    <mui.core.Box display="flex" flexDirection="column" style={{ height: '100%' }} ref={container}>
      <Tabular<SHEETS>
        data={
          data
            ? {
                [yearlySummarySheetKey]: data?.[yearlySummarySheetKey],
                [monthlySummarySheetKey]: data?.[monthlySummarySheetKey],
                [detailsSheetKey]: data?.[detailsSheetKey],
                [alphaDetailsSheetKey]: data?.[alphaDetailsSheetKey],
              }
            : null
        }
        getColumns={getColumnsObj}
        sheetTitles={{
          [yearlySummarySheetKey]: yearlySummarySheetName,
          [monthlySummarySheetKey]: monthlySummarySheetName,
          [detailsSheetKey]: detailsSheetName,
          [alphaDetailsSheetKey]: alphaDetailsSheetName,
        }}
        fullScreen={fullScreen}
        goFullScreen={goFullScreen}
        queryParams={queryParams}
        customError={error}
        preferences={{
          preferencesSourceWidgetId: id,
          preferenceKey: isBasket
            ? ts.enums.PREFERENCES_KEY_ENUM.BASKET_TABULAR_COLS
            : ts.enums.PREFERENCES_KEY_ENUM.BACKTEST_TABULAR_COLS,
          preferenceTab: isBasket
            ? ts.enums.PREFERENCES_INNER_TABS_ENUM.BASKET_SUMMARY_AND_DETAILS_COLS
            : ts.enums.PREFERENCES_INNER_TABS_ENUM.BACKTEST_SUMMARY_AND_DETAILS_COLS,
        }}
        tableParams={tableParams}
        setTableParams={{
          [yearlySummarySheetKey]: (v) => setTableParams((t) => ({ ...t, [yearlySummarySheetKey]: v })),
          [monthlySummarySheetKey]: (v) => setTableParams((t) => ({ ...t, [monthlySummarySheetKey]: v })),
          [detailsSheetKey]: (v) => setTableParams((t) => ({ ...t, [detailsSheetKey]: v })),
          [alphaDetailsSheetKey]: (v) => setTableParams((t) => ({ ...t, [alphaDetailsSheetKey]: v })),
        }}
        triggerWidthChange={params.fullWidth}
      />
    </mui.core.Box>
  );
};

export default Chart;
